import { IValidator } from './interfaces/IValidator.js';
import { ValidationResult } from './ValidationResult.js';

/**
 * Base validator implementing common validation logic (DRY principle)
 * Template Method pattern for consistent validation flow
 */
export class BaseValidator extends IValidator {
  constructor(config) {
    super();
    this.config = config;
  }

  /**
   * Template method defining validation algorithm
   */
  validate(id) {
    try {
      // Step 1: Basic validation
      const basicValidation = this.validateBasic(id);
      if (!basicValidation.isValid) {
        return basicValidation;
      }

      // Step 2: Format validation
      const formatValidation = this.validateFormat(id);
      if (!formatValidation.isValid) {
        return formatValidation;
      }

      // Step 3: Checksum validation
      const checksumValidation = this.validateChecksum(id);
      return checksumValidation;

    } catch (error) {
      return ValidationResult.invalid(`Validation error: ${error.message}`);
    }
  }

  /**
   * Basic validation - common to all ID types
   */
  validateBasic(id) {
    if (!id) {
      return ValidationResult.invalid('ID cannot be empty');
    }

    if (typeof id !== 'string') {
      return ValidationResult.invalid('ID must be a string');
    }

    const trimmedId = id.trim();
    if (trimmedId.length === 0) {
      return ValidationResult.invalid('ID cannot be empty');
    }

    return ValidationResult.valid();
  }

  /**
   * Format validation - to be implemented by subclasses
   */
  validateFormat(id) {
    throw new Error('validateFormat must be implemented by subclass');
  }

  /**
   * Checksum validation - to be implemented by subclasses
   */
  validateChecksum(id) {
    throw new Error('validateChecksum must be implemented by subclass');
  }

  /**
   * Helper method for regex validation (DRY)
   */
  validateRegex(id, pattern, errorMessage) {
    if (!pattern.test(id)) {
      return ValidationResult.invalid(errorMessage);
    }
    return ValidationResult.valid();
  }

  /**
   * Helper method for length validation (DRY)
   */
  validateLength(id, expectedLength, errorMessage) {
    if (id.length !== expectedLength) {
      return ValidationResult.invalid(errorMessage || `ID must be ${expectedLength} characters long`);
    }
    return ValidationResult.valid();
  }
}